import { Box } from '@mui/system';
import { getShortLanguageCode } from 'utils/format';
import React, { FC, Fragment, useEffect, useState } from 'react';
import AddressAutocomplete from './address-autocomplete/address-autocomplete.component';
import { Address, AddressFormDetails } from 'models/contact';
import CountryInput from '../country-input/country-input.component';
import { Country } from 'models/country';
import { Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { countryFromCode } from 'utils/country-helper';
import { TabContext, TabPanel } from '@mui/lab';
import PostBoxForm from './post-box-form/post-box-form.component';
import { googleStaticMapUrl } from 'utils/google-static-map';

type AddressFormProps = {
    onChange?: (address: Address | undefined) => void;
    title?: string;
    defaultCountryCode?: string;
    address?: Address;
    forceValidAddress?: boolean;
    disableChageAddress?: boolean;
};
const AddressForm: FC<AddressFormProps> = ({
    onChange,
    title,
    defaultCountryCode,
    address,
    disableChageAddress,
}: AddressFormProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'country-names']);
    const [selectedAddressDetails, setSelectedAddressDetails] = useState<AddressFormDetails>();
    const [selectedTab, setSelectedTab] = useState('address');
    const [selectedCountry, setSelectedCountry] = useState<Country>();
    const user = currentUserVar();

    const formattedStreet = (address: Address) => {
        let street = address.street;
        if (address.street2) {
            street += `, ${address.street2}`;
        }
        return street;
    };

    const formattedProvinceCountry = (address: Address) => {
        let country = translate(`country-names:COUNTRY_${address.country}`);
        if (address.stateProvince) {
            country = `${address.stateProvince}, ${country}`;
        }
        return country;
    };

    useEffect(() => {
        const country = countryFromCode(defaultCountryCode || 'SE');
        if (country) {
            setSelectedCountry(country);
        }
    }, [defaultCountryCode]);

    useEffect(() => {
        if (address) {
            setSelectedAddressDetails({ googleId: '', address: address, isCompleteAddress: true });
        }
    }, [address]);

    const formattedAddress = (address: Address) => {
        return (
            <Fragment>
                <Box mb={1}>
                    <Typography variant="h4">{title || translate('common:ADDRESS')}</Typography>
                </Box>
                <Box fontSize="1.1em">{formattedStreet(address)}</Box>
                <Box fontSize="1.1em">{`${address.postalCode} ${address.city}`}</Box>
                <Box fontSize="1.1em">{formattedProvinceCountry(address)}</Box>
            </Fragment>
        );
    };

    const handleAddressChange = (addressDetails: AddressFormDetails) => {
        setSelectedAddressDetails(addressDetails);
        if (addressDetails.isCompleteAddress && onChange) {
            onChange(addressDetails.address);
        }
    };

    const handleEditClick = () => {
        setSelectedAddressDetails(undefined);
        if (onChange) {
            onChange(undefined);
        }
    };

    const googleStaticMapSrc = (addressDetails: AddressFormDetails) =>
        googleStaticMapUrl(addressDetails.address.geoLocation, getShortLanguageCode(user.localeFormat?.language));

    return (
        <Fragment>
            <Paper variant="outlined">
                <Box p={2}>
                    {selectedAddressDetails && selectedAddressDetails.isCompleteAddress ? (
                        <Box display="flex">
                            <Box flexGrow={1}>
                                <Box mb={2}>{formattedAddress(selectedAddressDetails.address)}</Box>
                                {!disableChageAddress ? (
                                    <Box>
                                        <Button onClick={() => handleEditClick()} variant="contained" size="small">
                                            {translate('organization:CHANGE_ADDRESS')}
                                        </Button>
                                    </Box>
                                ) : undefined}
                            </Box>
                            <Box>
                                {selectedAddressDetails.address.geoLocation ? (
                                    <img src={googleStaticMapSrc(selectedAddressDetails)} alt="Google Map" />
                                ) : (
                                    <Fragment>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            flexDirection="column"
                                            sx={{ backgroundColor: '#eee', width: 290, height: 175, color: '#888' }}
                                        >
                                            <Box>{translate('organization:MISSING_GEO_LOCATION')}</Box>
                                        </Box>
                                    </Fragment>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Fragment>
                            <TabContext value={selectedTab}>
                                <Box mt={-1} mb={2}>
                                    <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)}>
                                        <Tab label={translate('common:ADDRESS')} value="address" />
                                        <Tab label={translate('common:POST_BOX')} value="postbox" />
                                    </Tabs>
                                </Box>
                                <Box mb={2}>
                                    <CountryInput
                                        country={
                                            selectedCountry || (countryFromCode(defaultCountryCode || 'SE') as Country)
                                        }
                                        onCountrySelect={(country) => setSelectedCountry(country)}
                                    />
                                </Box>
                                <TabPanel value="address" sx={{ padding: 0 }}>
                                    {selectedCountry ? (
                                        <AddressAutocomplete onChange={handleAddressChange} country={selectedCountry} />
                                    ) : undefined}
                                </TabPanel>
                                <TabPanel value="postbox" sx={{ padding: 0 }}>
                                    {selectedCountry ? (
                                        <PostBoxForm onChange={handleAddressChange} country={selectedCountry} />
                                    ) : undefined}
                                </TabPanel>
                            </TabContext>
                        </Fragment>
                    )}
                </Box>
            </Paper>
        </Fragment>
    );
};

export default AddressForm;
